import { Drawer } from 'components/Drawer'
import { DrawerProps } from 'components/Drawer/Root'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Button } from 'blaze-ui/Button'
import { Input } from 'almanac-ui/TextInputField/Input'
import { ArrowRefresh2 } from 'blaze-ui/icons'
import { observer } from 'mobx-react-lite'
import { useState, useEffect, useRef } from 'react'
import { useMutation } from 'react-query'
import { generateSocialCaptions } from 'components/Wizards/generateSocialCaptions'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { CaptionOptionsList } from 'components/Wizards/MobileWizards/MobileWizard/components/GeneratedContent/CaptionOptionsList'
import Styles from './EditCaptionDrawer.module.scss'

interface EditCaptionDrawerProps extends DrawerProps {
  initialText: string
  onSave: (newText: string) => void
  onCancel: () => void
}

export const EditCaptionDrawer = observer(
  ({ initialText, onSave, onCancel, ...drawerProps }: EditCaptionDrawerProps) => {
    const [draftText, setDraftText] = useState(initialText)
    const [captionOptions, setCaptionOptions] = useState<string[]>([])
    const [selectedOptionIndex, setSelectedOptionIndex] = useState<number>(-1)
    const hasGeneratedOnOpen = useRef(false)
    const workspaceId = useCurrentWorkspaceId()
    const generateCaptionsMutation = useMutation(generateSocialCaptions)

    useEffect(() => {
      if (drawerProps.open && initialText.trim() && !hasGeneratedOnOpen.current) {
        hasGeneratedOnOpen.current = true
        generateCaptionsMutation.mutate(
          {
            payload: {
              prompt: {
                topic: initialText,
                generation_type: ChannelType.InstagramPosts,
                count: 5,
              },
              workspaceId,
            },
          },
          {
            onSuccess: (captions) => {
              setCaptionOptions(captions)
            },
          },
        )
      }

      if (!drawerProps.open) {
        hasGeneratedOnOpen.current = false
      }
    }, [drawerProps.open, initialText, workspaceId]) // eslint-disable-line react-hooks/exhaustive-deps

    const handleSave = () => {
      onSave(draftText)
    }

    const handleCancel = () => {
      setDraftText(initialText)
      onCancel()
    }

    const handleRegenerate = () => {
      generateCaptionsMutation.mutate(
        {
          payload: {
            prompt: {
              topic: draftText || initialText,
              generation_type: ChannelType.InstagramPosts,
              count: 5,
            },
            workspaceId,
          },
        },
        {
          onSuccess: (captions) => {
            setCaptionOptions(captions)
          },
        },
      )
    }

    const handleOptionSelect = (index: number) => {
      setDraftText(captionOptions[index])
      setSelectedOptionIndex(index)
    }

    return (
      <Drawer.Root {...drawerProps}>
        <Drawer.Portal>
          <Drawer.Overlay />
          <Drawer.Content fullHeight>
            <Drawer.InnerContent isSnappedTop>
              <Drawer.Top>
                <Drawer.Back onPress={handleCancel} />
                <Drawer.Title asChild>
                  <Heading level={5}>Edit Caption</Heading>
                </Drawer.Title>
                <Drawer.Close position="right" />
              </Drawer.Top>
              <Drawer.Container>
                <div className={Styles.container}>
                  <div className={Styles.inputSection}>
                    <Input
                      value={draftText}
                      onChange={(e) => setDraftText(e.target.value)}
                      rows={7}
                      name="caption"
                      isTextarea
                      disabled={false}
                      aria-label="caption"
                    />

                    <div className={Styles.actionButtonsRow}>
                      <Button
                        className={Styles.actionButton}
                        variant="tertiary"
                        onPress={handleRegenerate}
                        frontIcon={() => <ArrowRefresh2 size={28} />}
                        isDisabled={generateCaptionsMutation.isLoading}
                      >
                        {generateCaptionsMutation.isLoading ? 'Loading additional caption options...' : 'Regenerate'}
                      </Button>
                    </div>
                  </div>

                  {captionOptions.length > 0 && (
                    <div className={Styles.optionsSection}>
                      <CaptionOptionsList
                        options={captionOptions}
                        selectedIndex={selectedOptionIndex}
                        onSelect={handleOptionSelect}
                        disabled={generateCaptionsMutation.isLoading}
                      />
                    </div>
                  )}

                  <div className={Styles.buttonContainer}>
                    <Button size="lg" variant="tertiary" fullWidth onPress={handleCancel}>
                      Cancel
                    </Button>
                    <Button
                      size="lg"
                      variant="primary"
                      fullWidth
                      onPress={handleSave}
                      isDisabled={draftText.trim().length === 0}
                    >
                      Save Changes
                    </Button>
                  </div>
                </div>
              </Drawer.Container>
            </Drawer.InnerContent>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    )
  },
)
